import { useEffect, useRef, useState } from 'react'
import { ArrowUp, ArrowUpRight, Check, Copy, Mail } from 'lucide-react'
import { useI18n } from '../i18n.jsx'
import FooterHandshake from './FooterHandshake.jsx'
import './footer.css'

const email = 'marketing@rti-tek.com'
const families = ['valve', 'panel', 'sensor', 'control', 'plug', 'water']

export default function Footer() {
  const { t, language } = useI18n()
  const [copied, setCopied] = useState(false)
  const [salesPressed, setSalesPressed] = useState(false)
  const salesPointer = useRef(null)
  const copyTimer = useRef(null)
  const mailto = (subject = t('site.email_subject_sales')) => `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(t('site.email_body'))}`

  useEffect(() => { setCopied(false); clearTimeout(copyTimer.current) }, [language])
  useEffect(() => () => clearTimeout(copyTimer.current), [])

  function releaseSales() {
    salesPointer.current = null
    setSalesPressed(false)
  }

  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 600px)')
    const release = () => releaseSales()
    const visibility = () => { if (document.hidden) release() }
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', release)
    window.addEventListener('blur', release)
    document.addEventListener('visibilitychange', visibility)
    mobile.addEventListener('change', release)
    return () => {
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', release)
      window.removeEventListener('blur', release)
      document.removeEventListener('visibilitychange', visibility)
      mobile.removeEventListener('change', release)
    }
  }, [])
  useEffect(() => { releaseSales() }, [language])

  function pressSales(event) {
    if (event.pointerType !== 'touch' || !event.isPrimary || !window.matchMedia('(max-width: 600px)').matches) return
    salesPointer.current = event.pointerId
    setSalesPressed(true)
  }

  function moveSales(event) {
    if (salesPointer.current !== event.pointerId) return
    const box = event.currentTarget.getBoundingClientRect()
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) releaseSales()
  }

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(email)
      setCopied(true)
      clearTimeout(copyTimer.current)
      copyTimer.current = setTimeout(() => setCopied(false), 2500)
    } catch {
      const address = document.getElementById('contact-email')
      const selection = window.getSelection()
      if (address && selection) {
        const range = document.createRange()
        range.selectNodeContents(address)
        selection.removeAllRanges()
        selection.addRange(range)
      }
    }
  }

  return <>
    <section id="contact" className="footer-cooperation rti-footer">
      <div className="section-inner">
        <div className="footer-invite">
          <div className="footer-invite-copy">
            <FooterHandshake />
            <div className="footer-invite-text">
              <div className="footer-invite-message">
                <h2>{t('site.footer_invite_title')}</h2>
                <p>{t('site.footer_invite_description')}</p>
              </div>
              <div className="email-row"><Mail className="footer-email-icon" size={18} aria-hidden="true" /><a id="contact-email" href={mailto()}>{email}</a><button type="button" className="icon-button" onClick={copyEmail} title={copied ? t('site.email_copied') : t('site.copy_email')} aria-label={copied ? t('site.email_copied') : t('site.copy_email')}>{copied ? <Check size={18} aria-hidden="true" /> : <Copy size={18} aria-hidden="true" />}</button></div>
            </div>
          </div>
          <div className="footer-invite-contact">
            <a className="button footer-sales-button" href={mailto()} data-pressed={salesPressed} onPointerDown={pressSales} onPointerMove={moveSales} onPointerUp={releaseSales} onPointerCancel={releaseSales} onPointerLeave={releaseSales} onLostPointerCapture={releaseSales} onContextMenu={event => { if (salesPointer.current !== null) event.preventDefault() }}>{t('site.footer_sales_button')}<span className="footer-send-flight" aria-hidden="true"><span className="footer-flight-trail" /><span className="footer-flight-trail" /><Mail className="footer-send-icon" size={18} /></span></a>
          </div>
        </div>
        <span className="sr-only" role="status">{copied ? t('site.email_copied') : ''}</span>
      </div>
    </section>
    <footer className="site-footer rti-footer">
    <div className="footer-contact">
      <div className="section-inner">
        <div className="footer-columns">
          <nav className="footer-link-column" aria-label={t('site.nav_products')}><h3>{t('site.nav_products')}</h3><a href="#products">{t('site.solutions_title')}</a>{families.map(id => <a key={id} href={`#product-${id}`}>{t(`site.${id}_title`)}</a>)}</nav>
          <div className="document-requests">
            <div className="footer-link-column"><h3>{t('site.catalogue_title')}</h3><p>{t('site.catalogue_description')}</p><a className="text-link" href={mailto(t('site.email_subject_catalogue'))}>{t('site.catalogue_button')}<ArrowUpRight size={17} aria-hidden="true" /></a></div>
            <div className="footer-link-column"><h3>{t('site.technical_title')}</h3><p>{t('site.technical_description')}</p><a className="text-link" href={mailto(t('site.email_subject_technical'))}>{t('site.technical_button')}<ArrowUpRight size={17} aria-hidden="true" /></a></div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-bottom-left">
            <div className="footer-brand-block">
              <a href="#top" className="brand" aria-label={t('site.home')}>Rti-Tek</a>
              <p>{t('site.footer_tagline')}</p>
              <address className="footer-company-details">
                <p>Email: <a href={`mailto:${email}`}>{email}</a></p>
                <p>Address: Room 802, Tower A, Huizhi R&amp;D Center, Longteng Community, Xixiang Street, Bao'an District, Shenzhen City, Guangdong Province, P. R. China.</p>
              </address>
            </div>
            <div className="footer-copyright">
              <span>© {new Date().getFullYear()} Rti-Tek {t('site.footer_rights')}</span>
              <span className="footer-version">0.1.0</span>
            </div>
          </div>
          <a className="footer-back-top" href="#top"><ArrowUp size={19} aria-hidden="true" /><span>{t('site.footer_back_top')}</span></a>
        </div>
      </div>
    </div>
  </footer>
  </>
}
